import { routerBack } from '@/src/common/utils/routerBack'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { useCancelScheduledTransfer } from '@/src/common/hooks/useCancelScheduledTransfer'
import * as CardPaymentsConstants from '@/src/experiences/card-payments/constants'
import * as CardActivity from '@/src/experiences/card-activity'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { CardPaymentsGql } from '@/src/experiences/card-payments/gql'
import type { AutoPayScheduleReviewComponentType } from '@/src/experiences/card-payments/screens/AutoPayScheduleReview'

function formatDateString(dateString: string) {
  if (!dateString) return
  const [year, month, day] = dateString.split('-')
  return `${month}-${day}-${year}` as const
}

export function LegacyAutoPayScheduleReview({
  AutoPayScheduleReviewComponent,
}: {
  AutoPayScheduleReviewComponent: AutoPayScheduleReviewComponentType
}) {
  const Alert = useAlert(LegacyAutoPayScheduleReview.name)
  const logError = useLogError()
  const { data: recurringScheduledPayment } = useLoggedInQuery(
    CardPaymentsGql.Queries.GET_RECURRING_SCHEDULED_PAYMENTS,
    { fetchPolicy: 'cache-and-network' },
  )
  const fetchedAutoPayInfo = recurringScheduledPayment?.getRecurringScheduledPayment

  const [cancelScheduledTransfer, { loading }] = useCancelScheduledTransfer()
  const nextScheduledTransferDate = fetchedAutoPayInfo?.nextScheduledTransferDate

  const nextScheduledTransferDateParsed = nextScheduledTransferDate
    ? formatDateString(nextScheduledTransferDate)
    : ''
  const bankAccount = `${fetchedAutoPayInfo?.transferSourceAccount?.bankName} - ${fetchedAutoPayInfo?.transferSourceAccount?.last4}`
  const parsedAmount = fetchedAutoPayInfo?.transferAmountStrategy
    ? CardActivity.Components.Highnote.ScheduledTransfers.calculatedTypeText[
        fetchedAutoPayInfo.transferAmountStrategy
      ]
    : ''

  function confirmRemoveAutoPay() {
    const scheduledTransferId = fetchedAutoPayInfo?.schedulePaymentId
    if (!scheduledTransferId) {
      return
    }

    Alert.alert(
      'Cancellation Notice',
      'Cancellation must be received by 9:00 AM Eastern on the date your payment is scheduled. Requests received after this will apply to the next scheduled payment.',
      [
        { text: 'Back', onPress: () => {}, style: 'cancel' },
        {
          text: CardPaymentsConstants.Strings.CancelAutoPay,
          onPress() {
            void cancelScheduledTransfer({
              variables: { scheduledTransferId },
              onCompleted({ cancelScheduledTransfer }) {
                cancelScheduledTransfer.__typename === 'MesaUserError'
                  ? Alert.alert(
                      'There was an issue canceling your auto-pay',
                      cancelScheduledTransfer.message,
                    )
                  : void routerBack()
              },
              onError(error) {
                Alert.alert('Error', 'Error while trying to remove auto-pay')
                logError(error)
              },
            })
          },

          style: 'destructive',
        },
      ],
    )
  }

  return (
    <AutoPayScheduleReviewComponent
      amount={parsedAmount}
      day={nextScheduledTransferDateParsed}
      account={bankAccount}
      confirmRemoveAutoPay={confirmRemoveAutoPay}
      loading={loading}
    />
  )
}
